<!--
  Monitor de órdenes de trabajo (Cazel).

  Todo el cálculo vive en el backend, en
  MonitorOrdenesTrabajoService (r114); este componente solo
  consume GET /MonitorOrdenesTrabajo/Estado y pinta el resultado.

  Todas las OT vigentes (con asignación viva) y su avance real: piezas
  elaboradas y faltantes, eficiencia, paro no programado, horas programadas,
  horas restantes al ritmo real y retraso proyectado contra el término del
  programa.

  Los filtros traen el catálogo COMPLETO de la planta (viene en la misma respuesta)
  y permiten teclear las primeras letras o números; al entrar a un campo se muestra
  el catálogo entero para poder cambiar de una selección a otra.
-->
<template>
  <v-col cols="12" class="pa-0">
    <div id="monitor-ordenes-trabajo">
      <div class="ot-head">
        <div>
          <h1>Monitor de órdenes de trabajo</h1>
          <div class="sub">{{ sub }}</div>
        </div>
        <div class="der">
          <span>{{ reloj }}</span>
          <button class="claro" @click="refrescar">Consultar</button>
          <button class="claro" @click="excel">Exportar Excel</button>
        </div>
      </div>
      <main>
        <div ref="error"></div>
        <div class="filtros">
          <div>
            <label>Área</label>
            <input v-model="fArea" list="ot_dl_area" placeholder="Todas" @input="pinta">
          </div>
          <div>
            <label>Estado de OT</label>
            <input v-model="fEstado" list="ot_dl_estado" placeholder="Todas" @input="pinta">
          </div>
          <div>
            <label>Máquina</label>
            <input v-model="fMaquina" list="ot_dl_maq" placeholder="Todas" @input="pinta">
          </div>
          <div>
            <label>Molde</label>
            <input v-model="fMolde" list="ot_dl_molde" placeholder="Todos" @input="pinta">
          </div>
          <div>
            <label>Artículo</label>
            <input v-model="fArticulo" list="ot_dl_art" placeholder="Todos" @input="pinta">
          </div>
          <div>
            <label>OT</label>
            <input v-model="fOt" list="ot_dl_ot" placeholder="Todas" @input="pinta">
          </div>
          <div>
            <label>Inicio desde</label>
            <input type="date" v-model="fIni" @change="pinta">
          </div>
          <div>
            <label>Término hasta</label>
            <input type="date" v-model="fFin" @change="pinta">
          </div>
          <div><button @click="limpia">Limpiar</button></div>
          <datalist id="ot_dl_area" ref="dlArea"></datalist>
          <datalist id="ot_dl_estado">
            <option value="activa"></option>
            <option value="por iniciar"></option>
            <option value="iniciada sin sesión"></option>
          </datalist>
          <datalist id="ot_dl_maq" ref="dlMaq"></datalist>
          <datalist id="ot_dl_molde" ref="dlMolde"></datalist>
          <datalist id="ot_dl_art" ref="dlArt"></datalist>
          <datalist id="ot_dl_ot" ref="dlOt"></datalist>
        </div>
        <div class="cards" ref="cards"></div>
        <div class="tabla"><table>
          <thead><tr ref="cab"></tr></thead><tbody ref="cuerpo"></tbody></table></div>
        <div class="pager" ref="pager"></div>
        <div class="nota" ref="nota"></div>
      </main>
    </div>
  </v-col>
</template>

<script>
import axios from "axios";

// Columnas de la tabla y de la exportación: [clave, título, numérica].
// Paginación: filtros y orden se aplican SIEMPRE al resultado completo; la
// página solo recorta lo que se pinta.
const POR_PAGINA = 20;
// Segundos que se conserva el resultado al volver a la pestaña, para no repetir una
// consulta cara cada vez que el usuario entra y sale (ver el watch de `active`).
const MIN_REFRESCO = 120;

const COLS = [
  ["maquina", "Máquina", 0], ["molde", "Molde", 0], ["ot", "OT", 0],
  ["articulo", "Artículo", 0], ["estado", "Estado", 0],
  ["plan", "Programadas", 1], ["producidas", "Piezas elaboradas", 1],
  ["faltantes", "Piezas faltantes", 1], ["eficiencia", "Eficiencia", 1],
  ["paro_noprog_h", "Paro no prog. (h)", 1], ["horas_prog", "Horas programadas", 1],
  ["horas_rest", "Horas restantes", 1], ["retraso_h", "Retraso proy. (h)", 1],
  ["inicio", "Inicio sesión", 0], ["entrega", "Término programa", 0],
];

export default {
  name: "ReporteMonitorOrdenesTrabajo",
  props: {
    active: { type: Boolean, default: true },
  },
  data() {
    return {
      sub: "cargando…",
      reloj: "",
      fArea: "", fEstado: "", fMaquina: "", fMolde: "", fArticulo: "", fOt: "",
      fIni: "", fFin: "",
      pagina: 1,
    };
  },
  mounted() {
    // El estado del tablero (D) y las filas filtradas (F) se guardan fuera de
    // data(): se repintan a mano y no necesitan reactividad.
    this.D = null;
    this.F = [];
    this.orden = { col: "retraso_h", asc: false };
    this.consultado = 0;
    this.tTic = setInterval(this.tic, 1000);
    this.$nextTick(this.preparaFiltros);
    this.cargar();
  },
  beforeDestroy() {
    clearInterval(this.tTic);
  },
  watch: {
    // Al volver a la pestaña se refresca, pero no en cada ida y vuelta: la consulta
    // recorre 120 días de historia y en Cazel TLT cuesta ~10 s contra el mismo
    // Core.API que atienden las tabletas. Si el dato tiene menos de MIN_REFRESCO
    // segundos se conserva; el botón «Consultar» siempre fuerza la consulta.
    active(v) {
      if (!v) return;
      const edad = (Date.now() - (this.consultado || 0)) / 1000;
      if (edad >= MIN_REFRESCO) this.cargar();
    },
  },
  methods: {
    f(n) { return n == null ? "—" : n.toLocaleString("es-MX"); },
    esc(s) {
      return String(s == null ? "" : s).replace(/&/g, "&amp;")
        .replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    },
    // Texto comparable: sin espacios sobrantes, en minúsculas y sin acentos.
    llano(s) {
      return String(s == null ? "" : s).trim().toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    },
    // El API serializa con Newtonsoft sin política de nombres (PascalCase); se
    // aceptan las dos capitalizaciones para no depender de esa configuración,
    // igual que el resto de los reportes del dashboard.
    val(o, ...nombres) {
      for (const n of nombres) {
        if (o && o[n] !== undefined && o[n] !== null) return o[n];
      }
      return undefined;
    },
    num(v) { return (v === undefined || v === null) ? null : Number(v); },
    // "2026-08-10T07:28:00" -> "2026-08-10 07:28" (el filtro y la tabla comparan
    // texto; así no se arrastra la zona horaria del navegador).
    fecha(v) { return v ? String(v).slice(0, 16).replace("T", " ") : ""; },
    async cargar() {
      this.reloj = "consultando…";
      try {
        const { data } = await axios.get("/MonitorOrdenesTrabajo/Estado");
        const v = this.val;
        const cats = v(data, "catalogos", "Catalogos") || {};
        this.D = {
          filas: (v(data, "filas", "Filas") || []).map(f => ({
            maquina: v(f, "maquina", "Maquina") || "",
            area: v(f, "area", "Area") || "SIN DESIGNAR",
            ot: v(f, "ot", "Ot") || "",
            articulo: v(f, "articulo", "Articulo") || "",
            molde: v(f, "molde", "Molde") || "",
            estado: v(f, "estado", "Estado") || "",
            plan: this.num(v(f, "plan", "Plan")),
            producidas: this.num(v(f, "producidas", "Producidas")),
            faltantes: this.num(v(f, "faltantes", "Faltantes")),
            eficiencia: this.num(v(f, "eficiencia", "Eficiencia")),
            ritmo_origen: v(f, "ritmoOrigen", "RitmoOrigen") || "sin referencia",
            ritmo_pct: this.num(v(f, "ritmoPorcentaje", "RitmoPorcentaje")),
            paro_noprog_h: this.num(v(f, "paroNoProgramadoHoras", "ParoNoProgramadoHoras")),
            horas_prog: this.num(v(f, "horasProgramadas", "HorasProgramadas")),
            horas_rest: this.num(v(f, "horasRestantes", "HorasRestantes")),
            retraso_h: this.num(v(f, "retrasoHoras", "RetrasoHoras")),
            inicio: this.fecha(v(f, "inicio", "Inicio")),
            entrega: this.fecha(v(f, "entrega", "Entrega")),
          })),
          cats: {
            areas: v(cats, "areas", "Areas") || [],
            maquinas: v(cats, "maquinas", "Maquinas") || [],
            moldes: v(cats, "moldes", "Moldes") || [],
            articulos: v(cats, "articulos", "Articulos") || [],
            ots: v(cats, "ots", "Ots") || [],
          },
          error: "",
        };
        this.consultado = Date.now();
        this.llenaCatalogos();
        this.pinta();
      } catch (e) {
        const msj = (e.response && e.response.data && e.response.data.title) || e;
        this.reloj = "";
        this.$refs.error.innerHTML =
          '<div class="err">No se pudo leer el reporte: ' + this.esc(msj) + "</div>";
      }
    },
    async refrescar() {
      await this.cargar();
    },
    llenaCatalogos() {
      const C = this.D.cats || {};
      const dl = (ref, vals) => {
        if (!this.$refs[ref]) return;
        this.$refs[ref].innerHTML = (vals || [])
          .map(x => '<option value="' + this.esc(x) + '">').join("");
      };
      // Áreas: catálogo completo del servidor + las que aparezcan en renglones.
      dl("dlArea", [...new Set([...(C.areas || []),
        ...this.D.filas.map(f => f.area)])].sort());
      dl("dlMaq", C.maquinas);
      dl("dlMolde", C.moldes);
      dl("dlArt", C.articulos);
      dl("dlOt", C.ots);
    },
    filtra() {
      // Se compara sin acentos: en el piso se teclea «iniciada sin sesion» o
      // «articulo» y el filtro tiene que encontrarlos igual. Sin esto, elegir
      // del desplegable funcionaba y teclear lo mismo a mano no devolvía nada.
      const b = this.llano;
      const a = b(this.fArea), e = b(this.fEstado), m = b(this.fMaquina),
        mo = b(this.fMolde), ar = b(this.fArticulo), ot = b(this.fOt),
        fi = this.fIni, ff = this.fFin;
      return this.D.filas.filter(f =>
        (!a || b(f.area).includes(a)) &&
        (!e || b(f.estado).includes(e)) &&
        (!m || b(f.maquina).includes(m)) &&
        (!mo || b(f.molde).includes(mo)) &&
        (!ar || b(f.articulo).includes(ar)) &&
        (!ot || b(f.ot).includes(ot)) &&
        (!fi || (f.inicio && f.inicio.slice(0, 10) >= fi)) &&
        (!ff || (f.entrega && f.entrega.slice(0, 10) <= ff)));
    },
    limpia() {
      this.fArea = ""; this.fEstado = ""; this.fMaquina = ""; this.fMolde = "";
      this.fArticulo = ""; this.fOt = ""; this.fIni = ""; this.fFin = "";
      this.pinta();
    },
    // El <datalist> filtra sus opciones contra lo que ya está escrito: con un valor
    // puesto solo se ve esa opción y no se puede cambiar a otra sin borrarla antes.
    // Al entrar al campo se vacía (sin refiltrar la tabla) para que salga el catálogo
    // completo; si se sale sin elegir, se devuelve el valor. Esc limpia ese filtro.
    preparaFiltros() {
      const campos = [
        ["fArea", "f_area"], ["fEstado", "f_estado"], ["fMaquina", "f_maq"],
        ["fMolde", "f_molde"], ["fArticulo", "f_art"], ["fOt", "f_ot"],
      ];
      const raiz = this.$el;
      if (!raiz) return;
      campos.forEach(([modelo], i) => {
        const el = raiz.querySelectorAll(".filtros input[list]")[i];
        if (!el) return;
        el.title = "Clic para ver todo el catálogo · Esc para limpiar este filtro";
        el.addEventListener("focus", () => {
          if (this[modelo]) { el.dataset.prev = this[modelo]; el.value = ""; }
        });
        el.addEventListener("input", () => { delete el.dataset.prev; });
        el.addEventListener("blur", () => {
          if (!el.value && el.dataset.prev) {
            el.value = el.dataset.prev;
            this[modelo] = el.dataset.prev;
          }
          delete el.dataset.prev;
        });
        el.addEventListener("keydown", e => {
          if (e.key === "Escape") {
            delete el.dataset.prev;
            el.value = ""; this[modelo] = ""; this.pinta(); el.blur();
          }
        });
      });
    },
    ordena(c) {
      this.orden = (this.orden.col === c)
        ? { col: c, asc: !this.orden.asc } : { col: c, asc: true };
      this.pagina = 1;                       // ordenar siempre lleva al principio
      this.pinta();
    },
    vePagina(n) { this.pagina = n; this.pinta(); },
    pinta() {
      const D = this.D;
      if (!D) return;
      const f = this.f, esc = this.esc;
      this.sub = "OT vigentes con asignación viva · consulta en solo lectura";
      // El pronóstico se apoya en la pareja máquina+molde. Si NINGUNA OT trae
      // molde, esta planta no codifica los moldes como el reporte espera y todos
      // los renglones saldrían en «sin referencia» sin decir por qué: más vale
      // decirlo. Es el aviso que respalda el ALCANCE declarado en el backend.
      const sinMolde = D.filas.length > 0 && D.filas.every(r => !r.molde);
      this.$refs.error.innerHTML = (D.error
        ? '<div class="err"><strong>La última consulta falló:</strong> ' +
          esc(D.error) + "</div>" : "")
        + (sinMolde
        ? '<div class="ojo"><strong>Sin molde en ninguna orden.</strong> Este reporte ' +
          "cruza máquina+molde para pronosticar el retraso, y en esta planta ninguna OT " +
          "trae molde asociado, así que no habrá pronóstico en ningún renglón. Revisar el " +
          "catálogo de moldes de la planta antes de usarlo.</div>" : "");

      const F = this.filtra();
      const { col, asc } = this.orden;
      F.sort((x, y) => {
        const a = x[col], b = y[col];
        if (a == null && b == null) return 0;
        if (a == null) return 1;
        if (b == null) return -1;
        return (a < b ? -1 : a > b ? 1 : 0) * (asc ? 1 : -1);
      });
      this.F = F;

      // se ordenó TODO el resultado; hasta aquí se recorta la página a la vista
      const paginas = Math.max(1, Math.ceil(F.length / POR_PAGINA));
      if (this.pagina > paginas) this.pagina = paginas;
      const desde = (this.pagina - 1) * POR_PAGINA;
      const vista = F.slice(desde, desde + POR_PAGINA);

      // Los tres estados se cuentan sin acentos, por lo mismo que el filtro, y
      // los tres tienen tarjeta: si «iniciada sin sesión» no la tuviera, esas OT
      // no aparecerían en ningún conteo y las tarjetas no sumarían el total.
      const b = this.llano;
      const act = F.filter(r => b(r.estado) === "activa").length,
        pin = F.filter(r => b(r.estado) === "por iniciar").length,
        sin = F.filter(r => b(r.estado) === "iniciada sin sesion").length,
        tarde = F.filter(r => r.retraso_h != null && r.retraso_h > 0).length,
        falt = F.reduce((s, r) => s + (r.faltantes || 0), 0);
      this.$refs.cards.innerHTML = [
        ["OT en la vista", F.length, ""], ["Activas", act, ""],
        ["Por iniciar", pin, ""],
        ["Iniciadas sin sesión", sin, ""],
        ["Con retraso proyectado", tarde, tarde ? "al" : ""],
        ["Piezas faltantes", f(falt), ""],
      ].map(([t, n, c]) => '<div class="card ' + c + '"><div class="n">' + n +
        '</div><div class="t">' + t + "</div></div>").join("");

      this.$refs.cab.innerHTML = COLS.map(([c, t, num]) =>
        '<th class="' + (num ? "num" : "") + '" data-col="' + c + '">' + t +
        (this.orden.col === c ? (this.orden.asc ? " ▲" : " ▼") : "") + "</th>")
        .join("");

      this.$refs.cuerpo.innerHTML = vista.length ? vista.map(r => {
        const tar = r.retraso_h != null && r.retraso_h > 0;
        return '<tr class="' + (tar ? "tarde" : "") + '">' +
          "<td><strong>" + esc(r.maquina) + '</strong><div class="mini">' +
            esc(r.area) + "</div></td>" +
          "<td>" + (esc(r.molde) || "—") + "</td>" +
          "<td>" + esc(r.ot) + "</td>" +
          "<td>" + (esc(r.articulo) || "—") + "</td>" +
          '<td><span class="eti ' + (r.estado === "activa" ? "act" :
            (r.estado === "por iniciar" ? "ini" : "sin")) + '">' +
            esc(r.estado) + "</span></td>" +
          '<td class="num">' + f(r.plan) + "</td>" +
          '<td class="num">' + f(r.producidas) + "</td>" +
          '<td class="num">' + f(r.faltantes) + "</td>" +
          '<td class="num">' + (r.eficiencia == null ? "—" : r.eficiencia + "%") + "</td>" +
          '<td class="num">' + (r.paro_noprog_h || "0") + "</td>" +
          '<td class="num">' + f(r.horas_prog) + "</td>" +
          '<td class="num">' + f(r.horas_rest) +
            '<div class="mini">' + esc(r.ritmo_origen) +
            (r.ritmo_pct ? " · " + r.ritmo_pct + "%" : "") + "</div></td>" +
          '<td class="num ' + (tar ? "rojo" : "verde") + '">' +
            (r.retraso_h == null ? "—"
              : (r.retraso_h > 0 ? "+" + f(r.retraso_h) : "a tiempo")) + "</td>" +
          '<td class="mini">' + (esc(r.inicio) || "—") + "</td>" +
          '<td class="mini">' + (esc(r.entrega) || "—") + "</td></tr>";
      }).join("")
        : '<tr><td colspan="' + COLS.length + '" class="vacio">' +
          "Ninguna OT cumple los filtros.</td></tr>";

      // pie de paginación
      const bot = (txt, n, act) => '<button type="button"' + (act ? "" : " disabled") +
        ' data-pag="' + n + '">' + txt + "</button>";
      this.$refs.pager.innerHTML = F.length
        ? "<span>" + (desde + 1) + "–" + (desde + vista.length) + " de " + F.length +
          " · página " + this.pagina + " de " + paginas + "</span>" +
          bot("« Primera", 1, this.pagina > 1) +
          bot("‹ Anterior", this.pagina - 1, this.pagina > 1) +
          bot("Siguiente ›", this.pagina + 1, this.pagina < paginas) +
          bot("Última »", paginas, this.pagina < paginas)
        : "";

      this.$refs.nota.innerHTML =
        "<strong>Cómo se calcula.</strong> <em>Eficiencia</em> = tiempo teórico de lo " +
        "producido (piezas × TC) entre el tiempo transcurrido desde el inicio de la " +
        "sesión de la OT. <em>Horas restantes</em> = piezas faltantes × TC ÷ el ritmo con " +
        "el que se pronostica: el de la propia OT sólo cuando ya lleva 30% de avance y, si " +
        "no, el que logró esa pareja máquina+molde en su OT anterior; sin ninguno de los " +
        "dos no se proyecta nada y el renglón dice «sin referencia». <em>Retraso proyectado</em> = " +
        "término estimado (ahora + horas restantes) contra la fecha de término del " +
        "programa de producción; en rojo si se proyecta tarde. <em>Paro no programado</em> " +
        "suma los paros bloqueantes no programados imputados a la OT, deduplicados por " +
        "cavidad. El <em>TC</em> es el tiempo de ciclo que el puente de QAD deja en la " +
        "plataforma (TiempoMinXUnidad, en segundos) y el molde sale del catálogo por " +
        "artículo. El reporte es de solo lectura: no abre, cierra ni corrige nada.";

      this.tic();
      this.$nextTick(this.enlazaOrden);
      this.$nextTick(this.enlazaPaginas);
    },
    // El encabezado se pinta con innerHTML, así que el clic para ordenar se
    // vuelve a enlazar en cada repintado.
    enlazaOrden() {
      const cab = this.$refs.cab;
      if (!cab) return;
      cab.querySelectorAll("th[data-col]").forEach(th => {
        th.onclick = () => this.ordena(th.getAttribute("data-col"));
      });
    },
    // El pie se pinta con innerHTML, así que el clic se reengancha en cada repintado.
    enlazaPaginas() {
      const pg = this.$refs.pager;
      if (!pg) return;
      pg.querySelectorAll("button[data-pag]").forEach(b => {
        b.onclick = () => { if (!b.disabled) this.vePagina(+b.getAttribute("data-pag")); };
      });
    },
    tic() {
      if (!this.consultado) return;
      const seg = Math.round((Date.now() - this.consultado) / 1000);
      this.reloj = "última consulta: hace " +
        (seg < 90 ? seg + " s" : Math.round(seg / 60) + " min");
    },

    // ---- Exportación a Excel: se arma un .xlsx de verdad (zip sin comprimir
    // con el XML de SpreadsheetML), sin librerías externas ni internet. ----
    crcTabla() {
      if (this._crcT) return this._crcT;
      const t = [];
      for (let n = 0; n < 256; n++) {
        let c = n;
        for (let k = 0; k < 8; k++) c = (c & 1) ? 0xEDB88320 ^ (c >>> 1) : c >>> 1;
        t[n] = c >>> 0;
      }
      this._crcT = t;
      return t;
    },
    crc(b) {
      const t = this.crcTabla();
      let c = 0xFFFFFFFF;
      for (let i = 0; i < b.length; i++) c = t[(c ^ b[i]) & 0xFF] ^ (c >>> 8);
      return (c ^ 0xFFFFFFFF) >>> 0;
    },
    zip(files) {                    // files = [[nombre, texto]]; sin comprimir
      const te = new TextEncoder(), partes = [], cen = [];
      let off = 0;
      const u16 = n => [n & 255, (n >> 8) & 255];
      const u32 = n => [n & 255, (n >> 8) & 255, (n >> 16) & 255, (n >>> 24) & 255];
      for (const [nom, txt] of files) {
        const nb = te.encode(nom), db = te.encode(txt), crc = this.crc(db);
        const cab = [80, 75, 3, 4, ...u16(20), ...u16(0x800), ...u16(0), ...u16(0),
          ...u16(0), ...u32(crc), ...u32(db.length), ...u32(db.length),
          ...u16(nb.length), ...u16(0)];
        partes.push(new Uint8Array(cab), nb, db);
        cen.push([80, 75, 1, 2, ...u16(20), ...u16(20), ...u16(0x800), ...u16(0),
          ...u16(0), ...u16(0), ...u32(crc), ...u32(db.length), ...u32(db.length),
          ...u16(nb.length), ...u16(0), ...u16(0), ...u16(0), ...u16(0), ...u32(0),
          ...u32(off), nb]);
        off += cab.length + nb.length + db.length;
      }
      let cenLen = 0;
      for (const c of cen) {
        const b = new Uint8Array(c.flatMap(x => x instanceof Uint8Array ? [...x] : [x]));
        partes.push(b);
        cenLen += b.length;
      }
      partes.push(new Uint8Array([80, 75, 5, 6, ...u16(0), ...u16(0),
        ...u16(files.length), ...u16(files.length), ...u32(cenLen), ...u32(off),
        ...u16(0)]));
      return new Blob(partes, {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
    },
    excel() {
      const esc = this.esc;
      const filas = [COLS.map(c => ({ v: c[1], s: 1 }))].concat(
        this.F.map(r => COLS.map(c => ({ v: r[c[0]] }))));
      const celdas = filas.map(cs => "<row>" + cs.map(c =>
        (typeof c.v === "number") ? '<c s="2"><v>' + c.v + "</v></c>"
          : '<c t="inlineStr"' + (c.s ? ' s="1"' : "") + "><is><t>" + esc(c.v) +
            "</t></is></c>").join("") + "</row>").join("");
      const NS = 'xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"';
      const hoja = '<?xml version="1.0" encoding="UTF-8"?><worksheet ' + NS + ">" +
        "<cols>" + COLS.map((c, i) => '<col min="' + (i + 1) + '" max="' + (i + 1) +
          '" width="' + (i === 0 || i === 3 ? 18 : 13) + '" customWidth="1"/>').join("") +
        "</cols><sheetData>" + celdas + "</sheetData></worksheet>";
      const estilos = '<?xml version="1.0" encoding="UTF-8"?><styleSheet ' + NS + ">" +
        '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font>' +
        '<font><b/><color rgb="FFFFFFFF"/><sz val="11"/><name val="Calibri"/></font></fonts>' +
        '<fills count="3"><fill><patternFill patternType="none"/></fill>' +
        '<fill><patternFill patternType="gray125"/></fill>' +
        '<fill><patternFill patternType="solid"><fgColor rgb="FF197F72"/></patternFill></fill></fills>' +
        '<borders count="1"><border/></borders>' +
        '<cellStyleXfs count="1"><xf/></cellStyleXfs>' +
        '<cellXfs count="3"><xf/><xf fontId="1" fillId="2" applyFont="1" applyFill="1"/>' +
        '<xf numFmtId="0"/></cellXfs></styleSheet>';
      const RN = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
      const libro = '<?xml version="1.0" encoding="UTF-8"?><workbook ' + NS +
        ' xmlns:r="' + RN + '"><sheets><sheet name="Monitor de OT" sheetId="1" ' +
        'r:id="rId1"/></sheets></workbook>';
      const b = this.zip([
        ["[Content_Types].xml", '<?xml version="1.0" encoding="UTF-8"?>' +
          '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
          '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
          '<Default Extension="xml" ContentType="application/xml"/>' +
          '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
          '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>' +
          '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>'],
        ["_rels/.rels", '<?xml version="1.0" encoding="UTF-8"?>' +
          '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
          '<Relationship Id="rId1" Type="' + RN + '/officeDocument" Target="xl/workbook.xml"/></Relationships>'],
        ["xl/workbook.xml", libro],
        ["xl/_rels/workbook.xml.rels", '<?xml version="1.0" encoding="UTF-8"?>' +
          '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
          '<Relationship Id="rId1" Type="' + RN + '/worksheet" Target="worksheets/sheet1.xml"/>' +
          '<Relationship Id="rId2" Type="' + RN + '/styles" Target="styles.xml"/></Relationships>'],
        ["xl/styles.xml", estilos],
        ["xl/worksheets/sheet1.xml", hoja],
      ]);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(b);
      a.download = "monitor_ot_" + new Date().toISOString().slice(0, 10) + ".xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
    },
  },
};
</script>

<style>
#monitor-ordenes-trabajo{--teal:#197F72;--teald:#0D564F;--rojo:#C00000;
 --ambar:#BF8F00;--azul:#2E74B5;--fondo:#f4f6f6;--tarjeta:#fff;--texto:#1c2321;
 --suave:#6b7a77;--linea:#dfe5e4;position:relative;background:var(--fondo);
 color:var(--texto);
 font:14px/1.45 -apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif;
 min-height:70vh}
#monitor-ordenes-trabajo *{box-sizing:border-box}
#monitor-ordenes-trabajo .ot-head{background:var(--teal);color:#fff;
 padding:14px 20px;display:flex;align-items:center;gap:16px;flex-wrap:wrap}
#monitor-ordenes-trabajo .ot-head h1{margin:0;font-size:17px;font-weight:600;
 color:#fff}
#monitor-ordenes-trabajo .sub{font-size:12px;opacity:.85}
#monitor-ordenes-trabajo .der{margin-left:auto;display:flex;align-items:center;
 gap:10px;font-size:12px}
#monitor-ordenes-trabajo button{font:inherit;border:0;border-radius:6px;
 padding:6px 12px;cursor:pointer;background:var(--teald);color:#fff}
#monitor-ordenes-trabajo button.claro{background:rgba(255,255,255,.18)}
#monitor-ordenes-trabajo button:hover{filter:brightness(1.12)}
#monitor-ordenes-trabajo main{padding:18px 20px 40px;max-width:1600px;margin:0 auto}
#monitor-ordenes-trabajo .filtros{display:flex;gap:8px;flex-wrap:nowrap;
 margin-bottom:14px;align-items:end;overflow-x:auto}
#monitor-ordenes-trabajo .filtros>div{flex:1 1 0;min-width:0}
#monitor-ordenes-trabajo .filtros label{display:block;font-size:10px;
 color:var(--suave);text-transform:uppercase;letter-spacing:.4px;
 margin-bottom:3px;white-space:nowrap}
#monitor-ordenes-trabajo .filtros input{font:inherit;font-size:13px;
 padding:6px 7px;border-radius:6px;border:1px solid var(--linea);
 background:var(--tarjeta);color:var(--texto);width:100%;min-width:0}
#monitor-ordenes-trabajo .filtros input[type=date]{min-width:118px}
/* Chrome solo pinta la flecha del catálogo cuando el campo tiene el foco: se deja
   fija para que se vea de entrada que todos los filtros son listas */
#monitor-ordenes-trabajo .filtros input[list]::-webkit-calendar-picker-indicator{opacity:1}
#monitor-ordenes-trabajo .cards{display:grid;
 grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:12px;
 margin-bottom:16px}
#monitor-ordenes-trabajo .card{background:var(--tarjeta);
 border:1px solid var(--linea);border-radius:10px;padding:11px 14px}
#monitor-ordenes-trabajo .card .n{font-size:24px;font-weight:600;line-height:1.1}
#monitor-ordenes-trabajo .card .t{font-size:11px;color:var(--suave);
 text-transform:uppercase;letter-spacing:.6px;margin-top:2px}
#monitor-ordenes-trabajo .card.al .n{color:var(--rojo)}
#monitor-ordenes-trabajo .card.am .n{color:var(--ambar)}
#monitor-ordenes-trabajo .tabla{background:var(--tarjeta);
 border:1px solid var(--linea);border-radius:10px;overflow-x:auto}
#monitor-ordenes-trabajo table{border-collapse:collapse;width:100%;
 font-size:12.5px}
#monitor-ordenes-trabajo th,#monitor-ordenes-trabajo td{padding:7px 9px;
 text-align:left;border-bottom:1px solid var(--linea);white-space:nowrap}
#monitor-ordenes-trabajo th{background:var(--teal);color:#fff;font-weight:600;
 font-size:10px;text-transform:uppercase;letter-spacing:.4px;cursor:pointer;
 user-select:none}
#monitor-ordenes-trabajo td.num,#monitor-ordenes-trabajo th.num{text-align:right;
 font-variant-numeric:tabular-nums}
#monitor-ordenes-trabajo tr.tarde{background:rgba(192,0,0,.06)}
#monitor-ordenes-trabajo .eti{display:inline-block;padding:2px 8px;
 border-radius:20px;font-size:10px;font-weight:600}
#monitor-ordenes-trabajo .act{background:rgba(25,127,114,.15);color:var(--teal)}
#monitor-ordenes-trabajo .ini{background:rgba(46,116,181,.15);color:var(--azul)}
#monitor-ordenes-trabajo .sin{background:rgba(191,143,0,.18);color:var(--ambar)}
#monitor-ordenes-trabajo .rojo{color:var(--rojo);font-weight:700}
#monitor-ordenes-trabajo .verde{color:var(--teal);font-weight:600}
#monitor-ordenes-trabajo .mini{font-size:10.5px;color:var(--suave)}
#monitor-ordenes-trabajo .nota{font-size:12px;color:var(--suave);margin-top:14px;
 line-height:1.6}
#monitor-ordenes-trabajo .err{background:rgba(192,0,0,.12);color:var(--rojo);
 padding:9px 12px;border-radius:8px;margin-bottom:14px;font-size:13px}
/* Aviso, no error: el reporte funciona, pero no puede pronosticar. */
#monitor-ordenes-trabajo .ojo{background:rgba(200,140,0,.14);color:#8a6200;
 padding:9px 12px;border-radius:8px;margin-bottom:14px;font-size:13px}
#monitor-ordenes-trabajo .vacio{padding:26px;text-align:center;color:var(--suave)}
#monitor-ordenes-trabajo .pager{display:flex;align-items:center;gap:8px;
 margin-top:10px;font-size:12px;color:var(--suave);flex-wrap:wrap}
#monitor-ordenes-trabajo .pager span{margin-right:auto}
#monitor-ordenes-trabajo .pager button{background:var(--tarjeta);color:var(--texto);
 border:1px solid var(--linea);padding:5px 10px;font-size:12px}
#monitor-ordenes-trabajo .pager button:disabled{opacity:.4;cursor:default}
#monitor-ordenes-trabajo .pager button:disabled:hover{filter:none}
</style>
